import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faSpinner,
  faArrowLeft,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import mammoth from "mammoth";
import "../../../../App.css";
import { swalWarning } from "../../../../utils/swalAlert";

const TermsOfServices = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Set page title
    document.title = "Terms of Services | GradeMe-AI";

    // Load DOCX content
    loadDocxContent();
  }, []);

  const loadDocxContent = async () => {
    try {
      setLoading(true);

      // Fetch the DOCX file from the same directory
      const response = await fetch("/files/tos.docx");

      if (!response.ok) {
        throw new Error("Failed to load Terms of Services document");
      }

      const arrayBuffer = await response.arrayBuffer();

      // Convert DOCX to HTML using mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer });

      if (result.messages.length > 0) {
        swalWarning(
          "Some formatting issues were detected while loading the Terms of Services. Please review the content carefully."
        );
      }

      setContent(result.value);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleReturn = () => {
    window.history.back();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const contentVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, duration: 0.8 },
    },
  };

  if (loading) {
    return (
      <div className="terms-container">
        <div className="terms-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading Terms of Services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="terms-container">
        <div className="terms-error">
          <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
          <h2>Error Loading Terms of Services</h2>
          <p>{error}</p>
          <button onClick={loadDocxContent} className="retry-button">
            Try Again
          </button>
          <button onClick={handleReturn} className="back-button">
            <FontAwesomeIcon icon={faArrowLeft} /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="terms-container">
      <motion.div
        className="terms-header"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 50 }}
      >
        <h1>
          <img src="/imgs/logo.svg" alt="logo" width={300} />
          Terms of Services
        </h1>
        <p className="terms-subtitle">
          Please read these terms carefully before using GradeMe-AI
        </p>
      </motion.div>

      <motion.div
        className="terms-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="terms-document" variants={contentVariants}>
          <div className="document-icon">
            <FontAwesomeIcon icon={faFileAlt} />
          </div>
          <div
            className="document-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </motion.div>

        {/* Agreement confirmation */}
        <motion.section
          className="terms-agreement"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
        >
          <p>
            By using GradeMe-AI, you acknowledge that you have read, understood,
            and agree to be bound by these Terms of Services.
          </p>
        </motion.section>
      </motion.div>
    </div>
  );
};

export default TermsOfServices;
